// src/utils/firebase.js
// =====================================================
// 🔥 Firebase – Production Safe (Rules Aligned)
// =====================================================

import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";

/* =====================================================
   🔹 FIREBASE CONFIG
   ===================================================== */

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

/* =====================================================
   🔹 INITIALIZE APP (SAFE – SINGLETON)
   ===================================================== */

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

/* =====================================================
   🔹 SERVICES
   ===================================================== */

export const auth = getAuth(app);
export const db = getFirestore(app);

/* =====================================================
   🔹 GOOGLE AUTH PROVIDER
   ===================================================== */

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: "select_account",
});

/* =====================================================
   🔹 AUTH HELPERS
   ===================================================== */

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    console.error("❌ Google Sign-In Error:", error);
    throw new Error("Unable to sign in with Google.");
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("❌ Logout Error:", error);
    throw new Error("Unable to log out.");
  }
};

/* =====================================================
   🔹 INTERNAL HELPERS
   ===================================================== */

const resolveCollection = (type = "") => {
  const key = type.toLowerCase();
  if (key === "chef") return "chefBookings";
  if (key === "dailyfood" || key === "cloudkitchen") return "dailyFoodBookings";
  return "cateringBookings";
};

/* =====================================================
   🔹 FIRESTORE HELPERS (RULES SAFE)
   ===================================================== */

/**
 * ✅ Add a booking
 * - Stores userId (MANDATORY for rules)
 */
export const addBooking = async (type, data, userId) => {
  if (!userId) throw new Error("User not authenticated.");
  if (!type || !data) throw new Error("Invalid booking data.");

  const collectionName = resolveCollection(type);

  const payload = {
    ...data,
    userId, // 🔥 MUST match Firestore rules
    status: data.status ?? "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, collectionName), payload);
  return docRef.id;
};

/**
 * 🔥 Realtime bookings listener (RULES ALIGNED)
 *
 * - User → only own bookings
 * - Admin → all bookings
 * - Never crashes if uid missing
 * - Always returns unsubscribe
 */
export const listenToBookings = (
  type,
  userId,
  callback,
  isAdmin = false
) => {
  if (typeof callback !== "function") {
    console.warn("listenToBookings: callback missing");
    return () => {};
  }

  // Prevent invalid queries
  if (!isAdmin && !userId) {
    console.warn("listenToBookings: blocked (no userId)");
    return () => {};
  }

  const collectionName = resolveCollection(type);
  const colRef = collection(db, collectionName);

  const q = isAdmin
    ? query(colRef)
    : query(colRef, where("userId", "==", userId)); // 🔥 FIXED

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      try {
        const bookings = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        callback(bookings);
      } catch (err) {
        console.error("❌ Snapshot parse error:", err);
        callback([]);
      }
    },
    (error) => {
      console.error("❌ Firestore listener error:", error);
    }
  );

  return unsubscribe;
};

/**
 * ✅ Update booking status
 * - Admin-only (rules enforced)
 */
export const updateBookingStatus = async (
  type,
  bookingId,
  newStatus,
  extra = {}
) => {
  if (!type || !bookingId || !newStatus) {
    throw new Error("Invalid booking update data.");
  }

  const collectionName = resolveCollection(type);
  const bookingRef = doc(db, collectionName, bookingId);

  await updateDoc(bookingRef, {
    status: newStatus,
    ...extra,
    updatedAt: serverTimestamp(),
  });
};
